// Standard/Node Modules
import Vue from 'vue'
import router from './router'
import store from './store'

// Vue Specific Configuration
import App from './App.vue'
import config from './config/document_config'
import dateConfig from './config/date-config'
import moment from 'moment'

// Third Party
import vuetify from './plugins/vuetify'
import VueGtag from 'vue-gtag'

// Auth0
import { domain, clientId, audience } from './auth/auth0-config.js'
import { Auth0Plugin } from './auth/auth0-plugin'

import * as Sentry from '@sentry/vue'
import { Integrations } from '@sentry/tracing'

const appEnv = process.env.VUE_APP_ENV

if (appEnv !== 'local') {
  Sentry.init({
    Vue: Vue,
    environment: appEnv,
    dsn: 'https://78b699d504ea4337b70ec2e4ea527e47@o461793.ingest.sentry.io/6079644',
    integrations: [new Integrations.BrowserTracing()],
    tracingOptions: {
      trackComponents: true
    },
    logErrors: true,
    tracesSampleRate: 1.0
  })
}

// Google Analytics 4 - Defensive Implementation (ARG tenant only, production only)
const gaId = process.env.VUE_APP_GA_ID
const isArgTenant = window.location.hostname.includes('arg')
const isProd = process.env.NODE_ENV === 'production'

if (gaId && isArgTenant && isProd) {
  try {
    Vue.use(
      VueGtag,
      {
        config: {
          id: gaId,
          params: {
            send_page_view: false // Router handles page views
          }
        },
        appName: 'ARG Document Portal',
        pageTrackerScreenviewEnabled: true,
        enabled: true,
        bootstrap: true,
        pageTrackerTemplate(to: any) {
          return {
            page_title: to.meta?.title || to.name,
            page_path: to.path
          }
        }
      },
      router
    )
  } catch (e) {
    console.warn('Google Analytics failed to initialize:', e)
  }
}

// GA Mock fallback - ensures $gtag never throws even if blocked/missing
if (!Vue.prototype.$gtag) {
  const isDev = appEnv === 'local'
  const noop = (action: string, ...args: any[]) => {
    if (isDev) {
      console.log(`[GA Mock] ${action}:`, ...args)
    }
  }
  Vue.prototype.$gtag = {
    event: (action: string, params?: object) => noop('event', action, params),
    pageview: (page: string | object) => noop('pageview', page),
    screenview: (screen: string | object) => noop('screenview', screen),
    customMap: (map: object) => noop('customMap', map),
    time: (params: object) => noop('time', params),
    exception: (params: object) => noop('exception', params),
    set: (params: object) => noop('set', params),
    config: (params: object) => noop('config', params),
    query: (...args: any[]) => noop('query', ...args)
  }
}

Vue.use(Auth0Plugin, {
  domain,
  clientId,
  audience,
  onRedirectCallback: (appState: any) => {
    router.push(
      appState && appState.targetUrl
        ? appState.targetUrl
        : window.location.pathname
    )
  }
})

export const eventbus = new Vue()

Vue.config.productionTip = false

// Instance Properties
Vue.prototype.$config = config.CONSTANTS
Vue.prototype.$dateConfig = dateConfig
Vue.prototype.moment = moment
Vue.prototype.eventbus = eventbus

new Vue({
  router,
  store,
  vuetify,
  render: h => h(App)
}).$mount('#app')
